<template>
  <div class="list-container">
    <div class="list-header">
      <h1>Organizations Admin</h1>
      <router-link to="/organizations/new" class="important-button big">
        <font-awesome-icon icon="plus" /> New Organization
      </router-link>
    </div>

    <div class="stats-panel">
      <div class="stats-container">
        <div class="stat-box">
          <div class="stat-title">TO</div>
          <div class="stat-value">{{ organizations.length }}</div>
          <div class="stat-description">Total Organizations</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'mno' }"
          @click="toggleFilter('mno')"
        >
          <div class="stat-title">MNO</div>
          <div class="stat-value">{{ monthlyNewOrganizations }}</div>
          <div class="stat-description">Monthly New Organizations</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'wno' }"
          @click="toggleFilter('wno')"
        >
          <div class="stat-title">WNO</div>
          <div class="stat-value">{{ weeklyNewOrganizations }}</div>
          <div class="stat-description">Weekly New Organizations</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'muo' }"
          @click="toggleFilter('muo')"
        >
          <div class="stat-title">MUO</div>
          <div class="stat-value">{{ multiUserOrganizations }}</div>
          <div class="stat-description">Multi User Organizations</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'olo' }"
          title="Organizations using over 10 GB of storage"
          @click="toggleFilter('olo')"
        >
          <div class="stat-title">OLO</div>
          <div class="stat-value">{{ overLimitOrganizations }}</div>
          <div class="stat-description">Over Limit Organizations</div>
        </div>
      </div>
      <div v-if="activeFilter" class="filter-indicator">
        <span>Filtering by: {{ getFilterName(activeFilter) }}</span>
        <button class="clear-filter" @click="clearFilter">Clear Filter</button>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Logo</th>
          <SortableListHeader
            column="name" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Name
          </SortableListHeader>
          <SortableListHeader 
            column="owner" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Owner & Members
          </SortableListHeader>
          <th>Channels</th>
          <SortableListHeader 
            column="files" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Files
          </SortableListHeader>
          <SortableListHeader 
            column="storage" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Storage
          </SortableListHeader>
          <SortableListHeader 
            column="created" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Created
          </SortableListHeader>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="sortedOrganizations.length === 0">
          <td colspan="9">No organizations found.</td>
        </tr>
        <tr 
          v-for="organization in sortedOrganizations" 
          :key="organization.id" 
          @click="handleRowClick($event, organization)" 
          class="clickable"
          :title="`View &quot;${organization.name}&quot;`"
        >
          <td>
            <ImageThumbnail 
              v-if="organization.logo_url" 
              :url="organization.logo_url" 
              max-width="80px" 
              max-height="50px" 
            />
          </td>
          <td class="overflow">
            <b :title="organization.name">{{ organization.name }}</b>
          </td>
          <td>
            <span v-if="!organization.owner && organization.collaborators.length === 0">-</span>
            <router-link 
              v-if="organization.owner" 
              :to="`/users/${organization.owner.id}`"
              :title="`View User &quot;${organization.owner.first_name} ${organization.owner.last_name}&quot; (${organization.owner.organization_roles.join(', ')})`"
              class="link-button"
            >
              <RoleBadges :roles="organization.owner.organization_roles" maxBadges="1" />
              {{ `${organization.owner.first_name} ${organization.owner.last_name}` }}
            </router-link>
            <div v-if="organization.collaborators.length > 0">
              <template v-for="(collaborator, index) in organization.collaborators" :key="collaborator.id">
                <router-link 
                  :to="`/users/${collaborator.id}`"
                  :title="`View User &quot;${collaborator.first_name} ${collaborator.last_name}&quot; (${collaborator.organization_roles.join(', ')})`"
                  class="link-button"
                >
                  <RoleBadges :roles="collaborator.organization_roles" maxBadges="1" />
                  {{ `${collaborator.first_name} ${collaborator.last_name}` }}
                </router-link>
                <br v-if="index < organization.collaborators.length - 1" />
              </template>
            </div>
          </td>
          <td>
            <span v-if="organization.channels && organization.channels.length > 0">
              <template v-for="(channel, index) in organization.channels" :key="channel.id">
                <router-link 
                  :to="`/channels/${channel.id}`" 
                  :title="`View Channel &quot;${channel.name}&quot;`"
                  class="link-button"
                >
                  {{ channel.name }}
                </router-link>
                <br v-if="index < organization.channels.length - 1" />
              </template>
            </span>
            <span v-else>-</span>
          </td>
          <td>{{ organization.fileCount == null ? '-' : organization.fileCount }}</td>
          <td>{{ organization.storage == null ? '-' : formatFileSize(organization.storage) }}</td>
          <td :title="formatDatetime(organization.created_at)">{{ formatDate(organization.created_at) }}</td>
          <td>
            <span class="actions">
              <router-link
                :to="`/organizations/${organization.id}/edit`"
                class="action-button edit"
                title="Edit"
              >
                <font-awesome-icon icon="edit" />
              </router-link>
              <button
                @click="confirmDelete(organization)"
                class="action-button delete"
                title="Delete"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import RoleBadges from '@/components/RoleBadges.vue';
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@/scripts/icons';
import { formatDate, formatDatetime, handleApiError, showConfirmDialog, formatFileSize } from '@/scripts/shared-utils';
import ImageThumbnail from '@/components/ImageThumbnail.vue';
import SortableListHeader from '@/components/SortableListHeader.vue';

const organizations = ref([]);
const activeFilter = ref(null);
const sortColumn = ref('created');
const sortDirection = ref('desc');
const router = useRouter();

// Handle sorting
const handleSort = (sortData) => {
  sortColumn.value = sortData.column;
  sortDirection.value = sortData.direction;
};

const fetchData = async () => {
  try {
    const response = await axios.get('/api/organizations');
    
    // Process each organization to add owner and collaborators properties
    const processedOrganizations = response.data.map(org => {
      // Find the owner (member with 'Owner' role)
      const owner = org.members && org.members.length > 0 ?
        org.members.find(member => 
          member.organization_roles && 
          member.organization_roles.includes('Owner')
        ) || null : null;
      
      // Get collaborators (all members except the owner)
      const collaborators = org.members && org.members.length > 0 ?
        org.members.filter(member => member !== owner) : [];

      let fileCount = null;
      let storage = null;
      if (org.channels && org.channels.length > 0) {
        fileCount = 0;
        
        for (const channel of org.channels) {
          if (channel.video_files && Array.isArray(channel.video_files)) {
            fileCount += channel.video_files.length;
            
            for (const file of channel.video_files) {
              if (file.file_size && !isNaN(file.file_size)) {
                storage = (storage || 0) + Number(file.file_size);
              }
            }
          }
        }
      }
      
      // Return the organization with added properties
      return {
        ...org,
        owner,
        collaborators,
        fileCount,
        storage
      };
    });
    
    organizations.value = processedOrganizations;
  } catch (error) {
    handleApiError(error, 'admin fetching organizations');
  }
};

const confirmDelete = async (organization) => {
  if (showConfirmDialog(`Are you sure you want to delete the organization "${organization.name}"?`)) {
    try {
      await axios.delete(`/api/organizations/${organization.id}`);
      organizations.value = organizations.value.filter(c => c.id !== organization.id);
    } catch (error) {
      handleApiError(error, 'admin deleting organization');
    }
  }
};

// Calculate time thresholds once for all computed properties
const getTimeThresholds = () => {
  const now = new Date();
  const monthAgo = new Date(now);
  monthAgo.setDate(now.getDate() - 28); // 28 days for monthly metrics
  
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7); // 7 days for weekly metrics
  
  return { monthAgo, weekAgo };
};

// Filter functions for each metric
const isMonthlyNewOrganization = (org) => {
  const { monthAgo } = getTimeThresholds();
  if (!org.created_at) return false;
  const createdAtDate = new Date(org.created_at);
  return createdAtDate >= monthAgo;
};

const isWeeklyNewOrganization = (org) => {
  const { weekAgo } = getTimeThresholds();
  if (!org.created_at) return false;
  const createdAtDate = new Date(org.created_at);
  return createdAtDate >= weekAgo;
};

const isMultiUserOrganization = (org) => {
  return org.members && org.members.length > 1;
};

const isOverLimitOrganization = (org) => {
  // 10GB = 10 * 1024 * 1024 * 1024 bytes
  const storageLimit = 10 * 1024 * 1024 * 1024;
  return org.storage && org.storage > storageLimit;
};

// Computed properties using the filter functions
const monthlyNewOrganizations = computed(() => {
  return organizations.value.filter(isMonthlyNewOrganization).length;
});

const weeklyNewOrganizations = computed(() => {
  return organizations.value.filter(isWeeklyNewOrganization).length;
});

const multiUserOrganizations = computed(() => {
  return organizations.value.filter(isMultiUserOrganization).length;
});

const overLimitOrganizations = computed(() => {
  return organizations.value.filter(isOverLimitOrganization).length;
});

// Filtered organizations based on active filter
const filteredOrganizations = computed(() => {
  if (!activeFilter.value) return organizations.value;
  
  switch (activeFilter.value) {
    case 'mno':
      return organizations.value.filter(isMonthlyNewOrganization);
    case 'wno':
      return organizations.value.filter(isWeeklyNewOrganization);
    case 'muo':
      return organizations.value.filter(isMultiUserOrganization);
    case 'olo':
      return organizations.value.filter(isOverLimitOrganization);
    default:
      return organizations.value;
  }
});

const sortedOrganizations = computed(() => {
  let sortedOrgs = filteredOrganizations.value ? [...filteredOrganizations.value] : [];
  
  sortedOrgs.sort((a, b) => {
    let comparison = 0;
    let lhs, rhs;
    
    switch (sortColumn.value) {
      case 'name':
        lhs = a.name || '';
        rhs = b.name || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'owner':
        lhs = a.owner ? `${a.owner.first_name} ${a.owner.last_name}` : '';
        rhs = b.owner ? `${b.owner.first_name} ${b.owner.last_name}` : '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'files':
        lhs = a.fileCount || 0;
        rhs = b.fileCount || 0;
        comparison = lhs - rhs;
        break;
        
      case 'storage':
        lhs = a.storage || 0;
        rhs = b.storage || 0;
        comparison = lhs - rhs;
        break;
        
      case 'created':
      default:
        lhs = new Date(a.created_at || 0).getTime();
        rhs = new Date(b.created_at || 0).getTime();
        comparison = lhs - rhs;
        break;
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });
  
  return sortedOrgs;
});

const toggleFilter = (filter) => {
  if (activeFilter.value === filter) {
    activeFilter.value = null;
  } else {
    activeFilter.value = filter;
  }
};

const clearFilter = () => {
  activeFilter.value = null;
};
const getFilterName = (filter) => {
  switch (filter) {
    case 'mno': return 'Monthly New Organizations';
    case 'wno': return 'Weekly New Organizations';
    case 'muo': return 'Multi User Organizations';
    case 'olo': return 'Over Limit Organizations';
    default: return '';
  }
};

const handleRowClick = (event, organization) => {
  const isInteractiveElement = event.target.closest('button, a, .actions, [role="button"]');
  if (!isInteractiveElement) {
    router.push(`/organizations/${organization.id}`);
  }
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-stats.css';
@import '@/styles/organization-members.css';
</style>